interface FlexColumn {
  id: string
  base: number
  max: number
  width: number
  capped: boolean
}

export interface FillColumnSpec {
  id: string
  // Floor the column grows from (its natural size).
  base: number
  // Lower bound an explicit override is clamped to.
  min: number
  // Ceiling the column grows to; a column with max === base never grows.
  max: number
  // The user's deliberate width, if any. Overridden columns are frozen out of
  // the flex (clamped to [min, max]) and contribute their fixed width to the
  // total instead of growing.
  override?: number
}

// Pure water-fill: given each column's base/min/max (plus any user override) and
// the container width, returns the pixel width for every column. Flex columns
// grow from base toward max, weighted by base; when one hits its ceiling it is
// capped and its leftover re-spreads to the rest. The rounding remainder is
// floored into the widest uncapped column so the total never exceeds
// containerWidth — overflowing would reintroduce the horizontal scrollbar the
// fill exists to remove. Lives apart from the React hook so the invariants are
// directly testable.
export function distributeColumnWidths(
  columns: FillColumnSpec[],
  containerWidth: number,
): Record<string, number> {
  const result: Record<string, number> = {}
  let resizedTotal = 0
  const flexColumns: FlexColumn[] = []

  columns.forEach(column => {
    if (column.override != null) {
      const fixed = Math.max(column.min, Math.min(column.max, column.override))
      result[column.id] = fixed
      resizedTotal += fixed
      return
    }
    flexColumns.push({
      id: column.id,
      base: column.base,
      max: column.max,
      width: column.base,
      capped: column.base >= column.max,
    })
  })

  const baseTotal = flexColumns.reduce((sum, column) => sum + column.base, 0)

  // Spread surplus over uncapped columns by base weight; when a column reaches
  // its ceiling, cap it and re-spread its leftover to the rest.
  let surplus = containerWidth - resizedTotal - baseTotal
  while (surplus > 0.5) {
    const open = flexColumns.filter(column => !column.capped)
    if (open.length === 0) {
      break
    }

    const openBase = open.reduce((sum, column) => sum + column.base, 0)
    let consumed = 0
    let cappedAny = false
    open.forEach(column => {
      const want = (surplus * column.base) / openBase
      const headroom = column.max - column.width
      if (want >= headroom) {
        column.width = column.max
        column.capped = true
        cappedAny = true
        consumed += headroom
      } else {
        column.width += want
        consumed += want
      }
    })

    surplus -= consumed
    if (!cappedAny) {
      break
    }
  }

  flexColumns.forEach(column => {
    result[column.id] = Math.round(column.width)
  })

  // Give the rounding remainder to the widest uncapped column, floored so the
  // surplus path never overflows the container — at most a sub-pixel gap.
  const absorber = flexColumns
    .filter(column => !column.capped)
    .sort((a, b) => b.base - a.base)[0]
  if (absorber) {
    const othersTotal = flexColumns
      .filter(column => column.id !== absorber.id)
      .reduce((sum, column) => sum + result[column.id], 0)
    result[absorber.id] = Math.max(
      absorber.base,
      Math.min(
        absorber.max,
        Math.floor(containerWidth - resizedTotal - othersTotal),
      ),
    )
  }

  return result
}
